import {Injectable, Injector, OnDestroy} from '@angular/core';
import {BehaviorSubject, Subscription} from 'rxjs';
import {AbstractControl, UntypedFormBuilder} from '@angular/forms';
import {DynamicFormService} from '../dynamic-form/dynamic-form.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {TranslateService} from '@ngx-translate/core';
import {ValidationError} from './validation-error';
import {FieldConfigWrapper} from '../dynamic-field/field-config-wrapper';
import {EventDispatcherService} from '../events/event-dispatcher.service';
import {FieldErrorNotifacation} from './field-notification';
;


@UntilDestroy()
@Injectable()
export class DynamicFormValidationService implements OnDestroy {

    private formStateSubject = new BehaviorSubject({
        valid: false,
        changed: false
    });
    public formState$ = this.formStateSubject.asObservable();

    private formErrorsSubject = new BehaviorSubject({});
    public formErrors$ = this.formErrorsSubject.asObservable();

    private errorKey: string;
    private subscription: Subscription;

    constructor(
        private readonly formBuilder: UntypedFormBuilder,
        private readonly injector: Injector,
        private readonly dynamicFormService: DynamicFormService,
        private readonly translateService: TranslateService,
        private readonly eventDispatcher: EventDispatcherService
    ) {
        this.dynamicFormService.formGroup$
            .pipe(untilDestroyed(this))
            .subscribe((value) => {
                this.subscription?.unsubscribe();

                this.subscription = value.statusChanges
                    .subscribe((status) => {
                        this.formStateSubject.next({
                            valid: (status === 'VALID'),
                            changed: true
                        });
                    });
            });

        this.eventDispatcher.onFieldError
            .pipe(untilDestroyed(this))
            .subscribe((value: FieldErrorNotifacation) => {
                const errors = this.formErrorsSubject.value;
                if (value.error) {
                    errors[value.config.name] = value.error;
                }
                if (!value.error) {
                    delete errors[value.config.name];
                }

                this.formErrorsSubject.next(errors);
            });
    }

    public validate() {
        if (this.dynamicFormService.formGroup.valid) {

            return true;
        }
        this.dynamicFormService.formGroup.markAllAsTouched();
        return false;
    }

    /**
     * A lifecycle hook that is called when a directive, pipe, or service is destroyed.
     */
    public ngOnDestroy(): void {
    }

    public getControlValidationError(fieldWrapper: FieldConfigWrapper<any>): ValidationError {
        const control = this.dynamicFormService.formGroup.get(fieldWrapper.config.name);
        const controlErrors = control.errors;
        if (!controlErrors || Object.keys(controlErrors).length === 0) {
            this.errorKey = null;

            return;
        }



        const errorKeys = Object.keys(controlErrors);
        //this.errorKey = errorKeys.find((key) => key !== CHIP_LIST_ERROR_KEY) ?? errorKeys[0];
        this.errorKey = errorKeys[0];
        return {errorKey: this.errorKey, errorMessage: this.translateError(control)};
    }

    private translateError(control: AbstractControl): string {
        switch (this.errorKey) {
            case 'isInteger':
                return this.translateService.instant(`FORM.fields.errors.${this.errorKey}`);
            case 'mydocmaCustomError':
                return this.translateService.instant(control.errors.mydocmaCustomError);
            case 'mydocmaCustomEmailError':
                return this.translateService.instant(control.errors.mydocmaCustomEmailError);
            case 'mydocmaCustomTimeError':
                return this.translateService.instant(control.errors.mydocmaCustomTimeError);
            case 'minlength':
                return this.translateService.instant(
                    `FORM.fields.errors.${this.errorKey}`,
                    {output: control.errors.minlength.requiredLength}
                );
            case 'max':
                return this.translateService.instant(
                    `FORM.fields.errors.${this.errorKey}`,
                    {output: control.errors.max.max}
                );
            case 'min':
                return this.translateService.instant(
                    `FORM.fields.errors.${this.errorKey}`,
                    {output: control.errors.min.min}
                );
            case 'maxlength':
                return this.translateService.instant(
                    `FORM.fields.errors.${this.errorKey}`,
                    {output: control.errors.maxlength.requiredLength}
                );
            case 'mydocmaCustomMaxSizeTotalError':
                const errorData = control.errors.mydocmaCustomMaxSizeTotalError;
                return this.translateService.instant(errorData.text, {size: errorData.formattedMaxSize });
            case 'textTriggerNested':
                return this.translateService.instant('FORM.fields.errors.textTriggerNested');
            case 'textTriggerInvalidOption':
                return this.translateService.instant('FORM.fields.errors.textTriggerInvalidOption');
            case 'textTriggerEmpty':
                return this.translateService.instant(
                    'FORM.fields.errors.textTriggerEmpty',
                    {triggerStart: control.errors.textTriggerEmpty.triggerStart, triggerEnd: control.errors.textTriggerEmpty.triggerEnd});
            case 'textTriggerUnclosedOrUnopened':
                return this.translateService.instant(
                    'FORM.fields.errors.textTriggerUnclosedOrUnopened',
                    {triggerStart: control.errors.textTriggerUnclosedOrUnopened.triggerStart, triggerEnd: control.errors.textTriggerUnclosedOrUnopened.triggerEnd});
            default :
                return this.translateService.instant(
                    `FORM.fields.errors.${this.errorKey}`
                );
        }
    }
}
